import { TeamBillingCache } from './billing';

export enum TeamRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
  CONSULTANT = 'consultant',
}

// The two legs of adminSetUserConsultant are not symmetric (GRANT is a single
// UpdateItem, REVOKE runs revokeConsultant's destructive sweep), so the
// mutation takes this instead of a Boolean. String-valued so it compares
// directly against the value GraphQL hands the resolver.
export enum ConsultantDesignationAction {
  GRANT = 'GRANT',
  REVOKE = 'REVOKE',
}

export const isTeamAdminOrOwner = (role: TeamRole | undefined): boolean =>
  role === TeamRole.OWNER ||
  role === TeamRole.ADMIN ||
  role === TeamRole.CONSULTANT;

// OWNER and CONSULTANT are excluded: both only originate from a dedicated flow, never an ordinary invite/update.
export const isAssignableTeamRole = (role?: string): role is TeamRole =>
  role === TeamRole.ADMIN ||
  role === TeamRole.MEMBER ||
  role === TeamRole.VIEWER;

// Opposite of BILLABLE_SEAT_ROLES — same-sounding concepts, not the same set.
export const TEAM_EDITOR_ROLES: ReadonlySet<TeamRole> = new Set([
  TeamRole.OWNER,
  TeamRole.ADMIN,
  TeamRole.MEMBER,
  TeamRole.CONSULTANT,
]);

export const isEditorRole = (role?: TeamRole): boolean =>
  role !== undefined && TEAM_EDITOR_ROLES.has(role);

// The set countTeamEditors bills as a seat — VIEWER and CONSULTANT never occupy one.
export const BILLABLE_SEAT_ROLES: ReadonlySet<TeamRole> = new Set([
  TeamRole.OWNER,
  TeamRole.ADMIN,
  TeamRole.MEMBER,
]);

export const isBillableSeatRole = (role?: TeamRole): boolean =>
  role !== undefined && BILLABLE_SEAT_ROLES.has(role);

// The Stripe-billed-seat filter (countTeamEditors, the AI-credits plan grant)
// — excludes VIEWER and CONSULTANT. Not the same set as TEAM_EDITOR_ROLES,
// which excludes only VIEWER.
export const isSeatRole = (role?: TeamRole): boolean =>
  role !== undefined &&
  role !== TeamRole.VIEWER &&
  role !== TeamRole.CONSULTANT;

export const CONSULTING_TEAM_MANAGER_ROLES: ReadonlySet<TeamRole> = new Set([
  TeamRole.OWNER,
  TeamRole.CONSULTANT,
]);

export const isConsultingTeamManagerRole = (role?: TeamRole): boolean =>
  role !== undefined && CONSULTING_TEAM_MANAGER_ROLES.has(role);

// Team-state gate for every consulting-team management action (the owner-invite
// carve-out, the consultant-invite carve-out, addConsultantToTeam and
// acceptConsultantInvite): the team must still be un-handed-off and the row
// passed in must hold a manager role on it. One predicate so the call sites
// cannot drift out of sync with each other.
export const canManageConsultants = (team: Team | null | undefined): boolean =>
  Boolean(team?.consultingTeam) &&
  !team?.trialHandedOffAt &&
  isConsultingTeamManagerRole(team?.userRole);

// An un-handed-off consulting team the user still owns counts as consultant-held too.
export const isConsultantHeldRow = (team: Team): boolean =>
  team.userRole === TeamRole.CONSULTANT ||
  (team.userRole === TeamRole.OWNER &&
    Boolean(team.consultingTeam) &&
    !team.trialHandedOffAt);

export interface TeamMember {
  id: string;
  name?: string;
  email?: string;
  title?: string;
}

// Billing fields are cached exclusively on the OWNER row (PK id=teamId, SK
// userId=ownerId); non-owner rows never carry them.
export interface Team extends TeamBillingCache {
  id?: string;
  userId?: string;
  userRole?: TeamRole;
  userFeatures?: string[];
  userName?: string;
  userEmail?: string;
  userTitle?: string;
  createdAt?: number;
  createdFor?: string;
  trialEndsAt?: number;
  name: string;
  hexColor?: string;
  // Set once on the anchor row at creation, never on any other row.
  consultingTeam?: boolean;
  // Absent means never handed off; present blocks a second trial reset and owner invite.
  trialHandedOffAt?: number;
}

// Neither field is sealed (plaintext booleans/numbers, not in teamCrypto's
// SEALED_ATTRIBUTES), so unlike TEAM_BILLING_CACHE_FIELDS these never need
// opening before a pick. Every ownership transfer must carry them across, or
// the anchor's designation and handoff history die on the row that receives it.
export const TEAM_ANCHOR_ONLY_FIELDS: readonly (keyof Team)[] = [
  'consultingTeam',
  'trialHandedOffAt',
];

export interface CreateTeamInput {
  name: string;
  hexColor?: string;
}

export interface UpdateTeamInput {
  id: string;
  name?: string;
  hexColor?: string;
}

export interface UpdateMemberInput {
  id: string;
  userId: string;
  userRole: TeamRole;
}

export interface GetPaginatedTeamMembersResponse {
  members: Team[];
  exclusiveStartKey: string | null;
}

export interface GroupedTeam {
  [key: string]: Team[];
}

export enum TeamInviteStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export interface TeamInvite {
  inviteeEmail: string; // Partition key
  teamId: string; // Sort key
  teamName: string;
  inviterUserId: string;
  inviterUserName: string;
  inviteeName: string;
  inviteeRole: TeamRole;
  time_to_live: number;
  lastSentAt: number;
  inviteStatus: TeamInviteStatus;
  invitedAt: number;
  respondedAt?: number;
  memberCount?: number;
}

// Whether the invited person was actually notified. UPDATED_NOT_SENT is the
// anti-spam window doing its job: the row changed (a new role, or a declined
// invite reopened) but no second email went out, so the caller must not report
// it as "invitation sent".
export enum TeamInviteOutcome {
  SENT = 'SENT',
  UPDATED_NOT_SENT = 'UPDATED_NOT_SENT',
  UNCHANGED = 'UNCHANGED',
}

export enum PromoteInviteeToOwnerOutcome {
  PROMOTED = 'PROMOTED',
  ALREADY_HANDED_OFF = 'ALREADY_HANDED_OFF',
}

// The invite-acceptance consumer is at-least-once, so a replay must resolve as ALREADY_HANDED_OFF rather than throw.
export type PromoteInviteeToOwnerResult =
  | { outcome: PromoteInviteeToOwnerOutcome.PROMOTED; team: Team }
  | { outcome: PromoteInviteeToOwnerOutcome.ALREADY_HANDED_OFF };

export enum TeamInviteFailureReason {
  MISSING_EMAIL = 'MISSING_EMAIL',
  SELF_INVITE = 'SELF_INVITE',
  ROLE_NOT_ASSIGNABLE = 'ROLE_NOT_ASSIGNABLE',
  TEAM_NOT_ACCESSIBLE = 'TEAM_NOT_ACCESSIBLE',
  ALREADY_MEMBER = 'ALREADY_MEMBER',
  // A CONSULTANT invite targeting an existing user who does not carry the
  // platform consultant designation. Distinct from ROLE_NOT_ASSIGNABLE, which
  // covers the inviter/team-state carve-out — this is about the invitee.
  INVITEE_NOT_CONSULTANT = 'INVITEE_NOT_CONSULTANT',
  UNKNOWN = 'UNKNOWN',
}

export interface TeamInviteSucceeded {
  __typename: 'TeamInviteSucceeded';
  invite: TeamInvite;
  outcome: TeamInviteOutcome;
}

export interface TeamInviteFailed {
  __typename: 'TeamInviteFailed';
  reason: TeamInviteFailureReason;
}

export type CreateInviteResult = TeamInviteSucceeded | TeamInviteFailed;
